import { useState, useEffect, useCallback } from 'react';
import {
  FileText, RefreshCw, Loader2, ChevronDown, ChevronRight,
  Tag, BarChart3, Layers, BookOpen, AlertTriangle, Target,
  Lightbulb, Hash, Users, Scissors,
} from 'lucide-react';
import ingestionService from '../api';

// Must match backend classify.py _DOMAIN_TYPES
const DOMAIN_ENTITY_TYPES = new Set([
  'REGULATION', 'FRAMEWORK', 'METHODOLOGY', 'RISK_CATEGORY', 'KEY_CONCEPT', 'METRIC',
]);

const QUALITY_DIM_LABELS = {
  text_length: 'Text Length',
  structure: 'Structure',
  readability: 'Readability',
  content_density: 'Content Density',
};

const DOMAIN_ENTITY_STYLE = {
  REGULATION:    { cls: 'bg-orange-50 text-orange-700 border-orange-200',  Icon: AlertTriangle },
  FRAMEWORK:     { cls: 'bg-blue-50 text-blue-700 border-blue-200',        Icon: BookOpen },
  METHODOLOGY:   { cls: 'bg-green-50 text-green-700 border-green-200',     Icon: Target },
  RISK_CATEGORY: { cls: 'bg-red-50 text-red-700 border-red-200',           Icon: AlertTriangle },
  KEY_CONCEPT:   { cls: 'bg-indigo-50 text-indigo-700 border-indigo-200',  Icon: Lightbulb },
  METRIC:        { cls: 'bg-purple-50 text-purple-700 border-purple-200',  Icon: BarChart3 },
};

const STD_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-sky-100 text-sky-700',
  'bg-teal-100 text-teal-700',
  'bg-green-100 text-green-700',
  'bg-yellow-100 text-yellow-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-purple-100 text-purple-700',
  'bg-violet-100 text-violet-700',
];

function hashColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return STD_COLORS[h % STD_COLORS.length];
}

function gradeBadge(total) {
  if (total >= 80) return { label: `Excellent (${total}/100)`, cls: 'bg-green-100 text-green-700 border border-green-200' };
  if (total >= 60) return { label: `Good (${total}/100)`,      cls: 'bg-blue-100 text-blue-700 border border-blue-200' };
  if (total >= 40) return { label: `Fair (${total}/100)`,      cls: 'bg-yellow-100 text-yellow-700 border border-yellow-200' };
  return               { label: `Poor (${total}/100)`,         cls: 'bg-red-100 text-red-700 border border-red-200' };
}

function QualityBar({ label, value, max = 25 }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500 w-28 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-12 text-right">{value}/{max}</span>
    </div>
  );
}

function TagRow({ tag }) {
  const path = tag.path?.length > 0 ? tag.path : [tag.name];
  const pct = Math.round((tag.confidence || 0) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 flex items-center gap-0.5 text-xs min-w-0 flex-wrap">
        {path.map((part, i) => (
          <span key={i} className="flex items-center gap-0.5">
            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />}
            <span className={i === path.length - 1
              ? 'font-semibold text-gray-800'
              : 'text-gray-500 shrink-0'}>
              {part}
            </span>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-16 bg-gray-100 rounded-full h-1.5">
          <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
      </div>
    </div>
  );
}

function DomainEntityPill({ entity }) {
  const style = DOMAIN_ENTITY_STYLE[entity.type] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Tag };
  const { cls, Icon } = style;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>
      <Icon className="w-3 h-3 shrink-0" />
      {entity.name}
    </span>
  );
}

function StandardEntityPill({ entity }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${hashColor(entity.name)}`}>
      {entity.name}
      {entity.mentions > 1 && <span className="opacity-60">×{entity.mentions}</span>}
    </span>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {children}
    </p>
  );
}

function DocCard({ doc }) {
  const [open, setOpen] = useState(false);
  const quality   = doc.quality   || {};
  const breakdown = quality.breakdown || {};
  const details   = quality.details   || {};
  const chunking  = doc.chunking  || {};
  const tags      = doc.tags      || [];
  const entities  = doc.entities  || [];

  const domainEntities   = entities.filter(e => DOMAIN_ENTITY_TYPES.has((e.type || '').toUpperCase()));
  const standardEntities = entities.filter(e => !DOMAIN_ENTITY_TYPES.has((e.type || '').toUpperCase()));

  const total  = quality.total ?? null;
  const badge  = total !== null ? gradeBadge(total) : null;
  const params = chunking.params || {};

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header row */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <FileText className="w-5 h-5 text-gray-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{doc.filename}</p>
          <p className="text-xs text-gray-400">
            {doc.doc_type?.toUpperCase() || 'DOCUMENT'} · {(doc.char_count || 0).toLocaleString()} chars
          </p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          {badge && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
          )}
          {tags.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Tag className="w-3.5 h-3.5" />{tags.length}
            </span>
          )}
          {(doc.entity_count ?? entities.length) > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Users className="w-3.5 h-3.5" />{doc.entity_count ?? entities.length}
            </span>
          )}
          {open
            ? <ChevronDown  className="w-4 h-4 text-gray-400" />
            : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4">

          {/* Quality + Chunking — two-column */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Quality Breakdown */}
            {Object.keys(breakdown).length > 0 && (
              <div>
                <SectionLabel icon={BarChart3}>Quality Breakdown</SectionLabel>
                <div className="space-y-2">
                  {Object.entries(breakdown).map(([key, val]) => (
                    <QualityBar
                      key={key}
                      label={QUALITY_DIM_LABELS[key] || key.replace(/_/g, ' ')}
                      value={val}
                      max={25}
                    />
                  ))}
                </div>
                {Object.keys(details).length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
                    {details.word_count !== undefined && (
                      <span className="text-xs text-gray-500">
                        Words: <span className="font-medium text-gray-700">{details.word_count.toLocaleString()}</span>
                      </span>
                    )}
                    {details.sentence_count !== undefined && (
                      <span className="text-xs text-gray-500">
                        Sentences: <span className="font-medium text-gray-700">{details.sentence_count}</span>
                      </span>
                    )}
                    {details.heading_count !== undefined && (
                      <span className="text-xs text-gray-500">
                        Headings: <span className="font-medium text-gray-700">{details.heading_count}</span>
                      </span>
                    )}
                    {details.unique_vocab_ratio !== undefined && (
                      <span className="text-xs text-gray-500">
                        Vocab ratio: <span className="font-medium text-gray-700">{Math.round(details.unique_vocab_ratio * 100)}%</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Chunking Strategy */}
            {chunking.label && (
              <div>
                <SectionLabel icon={Scissors}>Chunking Strategy</SectionLabel>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-sm font-semibold text-gray-800 mb-1">{chunking.label}</p>
                  {chunking.description && (
                    <p className="text-xs text-gray-500 mb-2.5 leading-relaxed">{chunking.description}</p>
                  )}
                  {Object.keys(params).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(params).map(([k, v]) => (
                        <span key={k} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded font-mono">
                          {k}={String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Classification Tags */}
          {tags.length > 0 && (
            <div>
              <SectionLabel icon={Tag}>Classification Tags</SectionLabel>
              <div className="space-y-2">
                {tags.map((tag, i) => <TagRow key={i} tag={tag} />)}
              </div>
            </div>
          )}

          {/* Domain Entities — domain-typed NER from classify/analysis */}
          {domainEntities.length > 0 && (
            <div>
              <SectionLabel icon={BookOpen}>Domain Entities ({domainEntities.length})</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {domainEntities.map((e, i) => <DomainEntityPill key={i} entity={{ ...e, name: e.name.replace(/\b\w/g, c => c.toUpperCase()) }} />)}
              </div>
            </div>
          )}

          {/* Standard Entities */}
          {standardEntities.length > 0 && (
            <div>
              <SectionLabel icon={Hash}>Standard Entities ({standardEntities.length})</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {standardEntities.map((e, i) => <StandardEntityPill key={i} entity={e} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClassifyView() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [classifyError, setClassifyError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ingestionService.getDocumentAnalysis();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClassify = async () => {
    setClassifying(true);
    setClassifyError(null);
    try {
      await ingestionService.classifyDocuments();
      await load();
    } catch (err) {
      setClassifyError(err.message);
    } finally {
      setClassifying(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      <span className="ml-2 text-sm text-gray-500">Loading classification analysis…</span>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
      Failed to load: {error}
      <button onClick={load} className="ml-3 underline">Retry</button>
    </div>
  );

  const docs             = data?.documents || [];
  const summary          = data?.summary   || {};
  const totalDocs        = summary.total_documents ?? docs.length;
  const classifiedCount  = summary.classified_count ?? docs.filter(d => d.tags?.length > 0).length;
  const unclassifiedCount = totalDocs - classifiedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Document Classification Analysis</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalDocs} document{totalDocs !== 1 ? 's' : ''} · {classifiedCount} classified
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClassify}
            disabled={classifying || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {classifying
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Classifying…</>
              : <><Layers className="w-3.5 h-3.5" /> Run Classification</>}
          </button>
          <button
            onClick={load}
            disabled={loading || classifying}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Classify error */}
      {classifyError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 flex items-center justify-between">
          <span>Classification failed: {classifyError}</span>
          <button onClick={() => setClassifyError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Banner: unclassified documents */}
      {totalDocs > 0 && unclassifiedCount > 0 && !classifying && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-amber-800">
            <span className="font-semibold">{unclassifiedCount} document{unclassifiedCount !== 1 ? 's' : ''}</span> not yet classified.
            Classification maps documents to taxonomy nodes and assigns confidence-scored tags.
            A taxonomy must exist before running classification.
          </p>
          <button
            onClick={handleClassify}
            className="shrink-0 text-xs font-medium text-amber-700 border border-amber-300 px-3 py-1.5 rounded hover:bg-amber-100"
          >
            Classify now
          </button>
        </div>
      )}


      {/* Summary cards */}
      {Object.keys(summary).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-lg font-bold text-gray-900">{v}</div>
              <div className="text-xs text-gray-500">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
            </div>
          ))}
        </div>
      )}

      {docs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No documents found. Scan a folder first, then run classification.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => <DocCard key={doc.id} doc={doc} />)}
        </div>
      )}
    </div>
  );
}
